import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Star, Truck, BadgeCheck } from "lucide-react";
import { PRODUCTS } from "../data/products";

const LINES = [
  { text: "The internet's", accent: false },
  { text: "best finds,", accent: true },
  { text: "hand-picked.", accent: false },
];

const EASE = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const featured = PRODUCTS[0];
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 16 });
  const glowX = useTransform(mx, [-0.5, 0.5], ["30%", "70%"]);
  const glowY = useTransform(my, [-0.5, 0.5], ["30%", "70%"]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 right-[-10%] h-[34rem] w-[34rem] rounded-full opacity-25 blur-3xl"
        style={{ background: "radial-gradient(circle, #FF9900 0%, transparent 65%)" }}
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 md:px-8 lg:grid-cols-2">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#FF9900]/30 bg-[#FF9900]/5 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-[#FFB800]"
            data-testid="hero-eyebrow"
          >
            rakeshsharma — curated storefront
          </motion.p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "text-[#FF9900]" : "text-slate-50"}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: EASE }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-md text-base text-slate-300 leading-relaxed"
          >
            We dig through thousands of Amazon listings so you don't have to.
            Every product here is researched, compared, and worth your money —
            across tech, home, and style.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-products-btn"
              onClick={() => document.querySelector("#products")?.scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#FF9900] px-7 py-3.5 text-sm font-bold text-[#0B0C0E] transition-all duration-200 hover:bg-[#FFB800] hover:shadow-[0_0_36px_rgba(255,153,0,0.35)] active:scale-95"
            >
              Explore the Finds
              <ArrowDown size={16} className="transition-transform duration-200 group-hover:translate-y-0.5" />
            </button>
            <div className="flex items-center gap-5 text-xs text-slate-400 font-mono">
              <span className="inline-flex items-center gap-1.5"><BadgeCheck size={14} className="text-[#FF9900]" /> Researched</span>
              <span className="inline-flex items-center gap-1.5"><Truck size={14} className="text-[#FF9900]" /> Prime eligible</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease: EASE }}
          className="relative [perspective:1200px]"
        >
          <motion.div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={() => { mx.set(0); my.set(0); }}
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="tracing-beam relative overflow-hidden rounded-2xl border border-white/10 bg-[#121418]"
            data-testid="hero-featured-card"
          >
            <motion.div
              aria-hidden
              className="pointer-events-none absolute inset-0 z-10"
              style={{
                background: useTransform(
                  [glowX, glowY],
                  ([x, y]) => `radial-gradient(420px circle at ${x} ${y}, rgba(255,153,0,0.16), transparent 70%)`
                ),
              }}
            />
            <img
              src={featured.image}
              alt={featured.title}
              className="h-[26rem] w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B0C0E] via-black/40 to-transparent p-6 pt-20">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFB800]">{featured.badge}</p>
              <p className="mt-1 font-display text-xl font-bold text-slate-50">{featured.title}</p>
            </div>
            <div
              className="absolute right-4 top-4 z-20 rounded-xl border border-white/10 bg-black/60 px-3.5 py-2 backdrop-blur-xl"
              style={{ transform: "translateZ(40px)" }}
            >
              <p className="font-mono text-lg font-bold text-[#FF9900]">{featured.price}</p>
            </div>
            <div
              className="absolute left-4 top-4 z-20 flex items-center gap-1.5 rounded-full border border-white/10 bg-black/60 px-3 py-1.5 backdrop-blur-xl"
              style={{ transform: "translateZ(30px)" }}
            >
              <Star size={12} className="fill-[#FF9900] text-[#FF9900]" />
              <span className="font-mono text-xs font-semibold text-slate-100">{featured.rating}</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
